// Library imports
import styled from "styled-components";
import { useSelector } from "react-redux";
import store, { RootState } from "store";
import { useEffect, useState } from "react";

// Local imports
import DownAngle from "../../../assets/img/icons/DownAngle.svg";
import UserListItem from "./UserListItem";
import Checkbox from "../../Common/Components/Checkbox";
import { IContactItem, IContactSorting } from "../Audience/interface";
import { ASSET_AUDIENCE } from "constants/actionTypes";

type UserListProps = {
  contacts: IContactItem[];
  hideIcons?: boolean;
  isLoading?: boolean;
  onCheckHandler?: (values: any) => void;
  clearSelection?: boolean;
  fromCampaign?: boolean;
  isSearch?: boolean;
  sortObj: IContactSorting;
  setSortObj: (sortObj: IContactSorting) => void;
};

const UserList: React.FC<UserListProps> = (props: UserListProps) => {
  //Props
  const { contacts, hideIcons, onCheckHandler, clearSelection, isSearch, sortObj, setSortObj } = props;

  // Local States
  const [checkedItems, setCheckedItems]: any[] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Redux States
  const campaignAudienceContactsLoading = useSelector(
    (state: RootState) => state.campaignAudience.contacts_loading
  );
  const campaignAudienceContactsSuccess = useSelector((state: RootState) => state.campaignAudience.success);
  const campaignAudienceContactsMessage: any = useSelector((state: RootState) => state.campaignAudience.message);

  // Local Variables
  const individualContacts = contacts;
  const emptyMessage =
    props.fromCampaign && isSearch
      ? "No Contacts Found."
      : props.fromCampaign
      ? "You haven’t added any contact to this campaign yet."
      : "No Contacts Found.";

  const handleSelectAll = () => {
    if (selectAll) {
      setCheckedItems([]);
      if (onCheckHandler) onCheckHandler([]);
    } else {
      const arr = individualContacts.map((item) => item.id !== null && item.id !== undefined && item.id);
      setCheckedItems([...arr]);
      if (onCheckHandler) onCheckHandler([...arr]);
    }

    setSelectAll(!selectAll);
  };

  const onClickChecked = (id: any) => {
    if (checkedItems.includes(id)) {
      let arr = [...checkedItems].filter((item) => item !== id);
      if (selectAll) setSelectAll(false);
      setCheckedItems([...arr]);
      if (onCheckHandler) {
        onCheckHandler(arr);
      }
    } else {
      setCheckedItems([...checkedItems, id]);
      if (onCheckHandler) onCheckHandler([...checkedItems, id]);
      if (!selectAll && [...checkedItems, id].length === individualContacts.length) setSelectAll(true);
    }
  };

  //  for clearing selection when clicked from delete bottom bar
  useEffect(() => {
    if (clearSelection && checkedItems.length > 0) {
      setCheckedItems([]);
      setSelectAll(false);
    }
  }, [clearSelection]);

  const resetCampaignContacts = () => {
    store.dispatch({
      type: ASSET_AUDIENCE.CONTACTS_LOAD,
    });
  };

  const clearSelectionHandler = () => {
    setCheckedItems([]);
    setSelectAll(false);
  };

  const sortHandler = (sortingColumn: string) => {
    clearSelectionHandler(); // clear any selected contacts
    resetCampaignContacts(); // reset contacts
    if (sortingColumn === "engagement_score") {
      if (sortObj.sortType === "ASC") {
        setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by ascending
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by descending
      }
    } else {
      if (sortObj.sortBy === sortingColumn) {
        if (sortObj.sortType === "") {
          setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by ascending
        } else if (sortObj.sortType === "ASC") {
          setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by descending
        } else {
          setSortObj({ sortBy: "engagement_score", sortType: "DESC" }); // reset  sorting to default
        }
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" });
      }
    }
  };

  return (
    <Wrapper campaignAudienceContactsLoading={campaignAudienceContactsLoading}>
      {individualContacts?.length > 0 ? (
        <div id="scrollableDiv" className="scrollable-table">
          <table>
            <thead>
              <tr>
                <td className="checkbox">
                  <Checkbox checked={selectAll} onChange={handleSelectAll} />
                </td>
                <td className="h-name">
                  <span className="sortable-td" onClick={() => sortHandler("contact_name")}>
                    <span className="left-align">Name</span>
                    <span
                      className={`upArrow arrow-opacity ${
                        sortObj.sortBy === "contact_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                    <span
                      className={`downArrow arrow-opacity ${
                        sortObj.sortBy === "contact_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                  </span>
                </td>
                <td className="company-col">
                  <span className="sortable-td" onClick={() => sortHandler("company_name")}>
                    <span className="left-align">Company</span>
                    <span
                      className={`upArrow arrow-opacity ${
                        sortObj.sortBy === "company_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                    <span
                      className={`downArrow arrow-opacity ${
                        sortObj.sortBy === "company_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                  </span>
                </td>
                <td className="personality-col">
                  <span className="sortable-td" onClick={() => sortHandler("personality_name")}>
                    <span className="left-align">Personality</span>
                    <span
                      className={`upArrow arrow-opacity ${
                        sortObj.sortBy === "personality_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                    <span
                      className={`downArrow arrow-opacity ${
                        sortObj.sortBy === "personality_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                  </span>
                </td>
                <td className="status-col">
                  <span className="sortable-td" onClick={() => sortHandler("status")}>
                    <span className="left-align">Status</span>
                    <span
                      className={`upArrow arrow-opacity ${
                        sortObj.sortBy === "status" && sortObj.sortType === "DESC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                    <span
                      className={`downArrow arrow-opacity ${
                        sortObj.sortBy === "status" && sortObj.sortType === "ASC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                  </span>
                </td>
                <td className="icon-col">&nbsp;</td>
                <td className="last-item">
                  <span className="sortable-td" onClick={() => sortHandler("engagement_score")}>
                    <span className="engagement-float">Engagement Score</span>
                    <span
                      className={`upArrow engagement-arrow arrow-opacity ${
                        sortObj.sortBy === "engagement_score" && sortObj.sortType === "DESC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                    <span
                      className={`downArrow engagement-arrow arrow-opacity ${
                        sortObj.sortBy === "engagement_score" && sortObj.sortType === "ASC" ? "transparent" : ""
                      }`}
                    >
                      <img src={DownAngle} alt="Sorting" />
                    </span>
                  </span>
                </td>
              </tr>
            </thead>
            <tbody>
              {individualContacts?.map((contact: any, index: number) => {
                return (
                  // <div key={contact.email}>{contact.email}</div>
                  <UserListItem
                    isProfiled={true}
                    key={index}
                    contact={contact}
                    isChecked={checkedItems.includes(contact.id)}
                    onClickChecked={() => onClickChecked(contact.id)}
                    hideIcons={hideIcons}
                    hideEllipsesMenu={true}
                    status={contact.status}
                    index={index}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : campaignAudienceContactsLoading ? null : campaignAudienceContactsSuccess ? (
        emptyMessage
      ) : (
        campaignAudienceContactsMessage
      )}
    </Wrapper>
  );
};

export default UserList;

const Wrapper = styled.div<{ campaignAudienceContactsLoading: boolean }>`
  margin-bottom: 25px;
  /*  */
  .sortable-td {
    cursor: ${(props) => (props.campaignAudienceContactsLoading ? "auto" : "pointer")};
    pointer-events: ${(props) => (props.campaignAudienceContactsLoading ? "none" : "auto")};
  }
  .arrow-opacity {
    opacity: ${(props) => (props.campaignAudienceContactsLoading ? "0.5" : "1")};
  }
  .transparent {
    opacity: 0.5;
  }
  .engagement-float {
    float: revert !important;
  }
  td {
    span:first-child {
      &.left-align {
        float: left;
        margin-top: -7px;
      }
    }
  }
  .upArrow {
    width: 10px;
    height: 8px;
    margin-top: -16px;
    margin-left: 5px;
    float: left;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: -12px;
    }
    img {
      transform: rotate(180deg);
    }
    &.engagement-arrow {
      float: right;
      margin-top: -9px;
      @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: -8px;
      }
    }
  }
  .downArrow {
    width: 10px;
    height: 8px;
    float: left;
    margin-left: 5px;
    margin-top: 8px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 7px;
    }
    img {
      float: left;
    }
    &.engagement-arrow {
      float: right;
      margin-top: 15px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 11px;
      }
      img {
        float: right;
      }
    }
  }
  .checked {
    border: 1px solid blue;
    border-radius: 10px;
  }
  .scrollable-table {
    position: relative;
    box-sizing: border-box;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 50px;
      margin-bottom: 0px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 40px;
      }
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    > div {
      overflow: hidden;
    }
    table {
      width: 100%;
      border: none;
      margin-bottom: 20px;
      position: relative;
      border-bottom: 1px solid #dddddd;
      &:before {
        content: "";
        position: absolute;
        width: 100%;
        height: calc(100% + 3px);
        /* border-radius: 8px; */
        transition-duration: 0.2s;
        box-sizing: border-box;
        margin-top: -2px;
        height: 1px;
        @media all and (min-width: 1440px) {
          margin-top: -3px;
        }
      }
      &.active-new {
        background: #cdf1d9;
        border-radius: 8px;
        border-bottom: none;
        &:before {
          border: 3px solid #27ce0a;
          border-radius: 8px;
          height: calc(100% + 2px);
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            height: 69px;
          }
          /* Safari (from 6.1 to 10)  */
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
          /* Safari (10.1+)  */
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
        }
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.active {
        background: #f9f8f6;
        border-radius: 8px;
        /* box-shadow: 0 0 0 3px #27CE0A; */
        /* border-bottom: none; */
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.drag-active {
        position: relative;
        border-radius: 8px;
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
        &:after {
          content: "";
          height: 100%;
          width: calc(100% + 10px);
          position: absolute;
          top: 0;
          left: 0;
          z-index: 99999;
          background-color: #f2efeb;
          border-radius: 8px;
          margin: 0 -5px;
          display: block;
        }
      }
      td {
        padding: 9px 10px;
        &.checkbox {
          width: 10px;
          padding-right: 0 !important;
        }
        &.name-col {
          width: 472px;
        }
        .profile-img-wrapper {
          /* border: 2px solid transparent; */
          padding: 2px;
          width: 54px;
          height: 54px;
          border-radius: 100%;
          float: left;
          overflow: hidden;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 41px;
            height: 41px;
          }
          .profile-img {
            border-radius: 100%;
            width: 100%;
            height: 100%;
          }
        }
        .profile {
          /* float: left; */
          padding-left: 14px;
          text-align: left;
          width: calc(100% - 50%);
          display: flex;
          flex-direction: column;
          .hidden-contact-name {
            height: 1px;
            overflow: hidden;
            display: block;
            opacity: 0;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
          }
          .name {
            color: #000000;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
            margin-bottom: -5px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              max-height: 38px;
            }
          }
        }
        td {
          color: #5e7079;
          font-size: 18px;
          font-family: "robotomedium";
          font-weight: 500;
          white-space: nowrap;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &.h-name {
            padding-left: 75px;
            /* width: 250px; */
            width: 472px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              padding-left: 65px;
            }
            @media all and (max-width: 1440px) {
              width: 450px;
            }
            @media all and (max-width: 1145px) {
              width: 200px;
            }
          }
          &.company-col {
            /* width: 120px; */
            width: 270px;
            @media all and (max-width: 1145px) {
              width: 100px;
            }
          }
          &.personality-col {
            min-width: 127px;
          }
          &.status-col {
            min-width: 118px;
          }
          &.icon-col {
            min-width: 150px;
          }
          &.last-item {
            text-align: right;
            padding-right: 45px;
            padding-top: 0;
            min-width: 223px;
            @media all and (max-width: 1590px) {
              min-width: 226px;
            }
          }
          &.industry {
            display: flex;
            justify-content: center;
          }
        }
      }
      tr {
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        border-bottom: 1px solid #dddddd;
        &:before {
          content: "";
          position: absolute;
          width: 100%;
          height: calc(100% + 3px);
          /* border-radius: 8px; */
          transition-duration: 0.2s;
          box-sizing: border-box;
          margin-top: -2px;
          height: 1px;
          @media all and (min-width: 1440px) {
            margin-top: -3px;
          }
        }
        &.active-new {
          background: #cdf1d9;
          border-radius: 8px;
          border-bottom: none;
          &:before {
            border: 3px solid #27ce0a;
            border-radius: 8px;
            height: calc(100% + 2px);
            _::-webkit-full-page-media,
            _:future,
            :root .safari_only {
              height: 69px;
            }
            /* Safari (from 6.1 to 10)  */
            @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
              @media {
                /* Your Class */
                height: 69px;
              }
            }
            /* Safari (10.1+)  */
            @media not all and (min-resolution: 0.001dpcm) {
              @media {
                /* Your Class */
                height: 69px;
              }
            }
          }
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
        }
        &.active {
          background: #f9f8f6;
          border-radius: 8px;
          /* box-shadow: 0 0 0 3px #27CE0A; */
          /* border-bottom: none; */
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
        }
        &.drag-active {
          position: relative;
          border-radius: 8px;
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
          &:after {
            content: "";
            height: 100%;
            width: calc(100% + 10px);
            position: absolute;
            top: 0;
            left: 0;
            z-index: 99999;
            background-color: #f2efeb;
            border-radius: 8px;
            margin: 0 -5px;
            display: block;
          }
        }
        td {
          padding: 9px 10px;
          &.checkbox {
            width: 10px;
            padding-right: 0 !important;
          }
          &.name-col {
            width: 472px;
          }
          &.actions-td {
            min-height: 90px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              min-height: 72px;
            }
          }
          .profile-img-wrapper {
            /* border: 2px solid transparent; */
            padding: 2px;
            width: 54px;
            height: 54px;
            border-radius: 100%;
            float: left;
            overflow: hidden;
            margin-top: 10px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              width: 41px;
              height: 41px;
              margin-top: 5px;
            }
            .profile-img {
              border-radius: 100%;
              width: 100%;
              height: 100%;
            }
          }
          .profile {
            float: left;
            padding-left: 14px;
            text-align: left;
            /* width: calc(100% - 50%); */
            /* display: flex;
          flex-direction: column; */
            /* width: 250px;
          max-width: calc(100% - 55px); */
            width: 250px;
            max-width: calc(100% - 55px);
            @media all and (max-width: 1281px) {
              width: 200px;
            }
            .hidden-contact-name {
              height: 1px;
              overflow: hidden;
              display: block;
              opacity: 0;
              font-size: 20px;
              font-family: "Roboto Slab", serif;
            }
            .name {
              color: #000000;
              font-size: 20px;
              font-family: "Roboto Slab", serif;
              margin-bottom: -5px;
              float: unset;
              display: initial;
              align-items: unset;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 14px;
              }
            }
            .email {
              color: #27ce0a;
              font-size: 14px;
              font-family: "robotomedium";
              font-weight: 500;
              margin-bottom: 0;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 10px;
              }
              &.designation {
                color: rgba(0, 0, 0, 0.8);
              }
            }
          }
          .organization {
            display: block;
            width: 100%;
            color: #424b50;
            font-size: 18px;
            font-family: "robotomedium";
            font-weight: 500;
            white-space: nowrap;
            text-overflow: ellipsis;
            /* max-width: 100%; */
            /* width: 120px;
          max-width: calc(100% - 15px); */
            width: 150px;
            max-width: calc(100% - 50px);
            overflow: hidden;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 13px;
            }
            @media all and (max-width: 1281px) {
              width: 100px;
            }
          }
          .personality,
          .status {
            color: #424b50;
            font-size: 18px;
            font-family: "robotomedium";
            font-weight: 500;
            &.status-style-sub {
              color: #1cb84d;
              font-weight: bold;
            }
            &.status-style-unsub {
              color: #d50508;
              font-weight: bold;
            }
            &.status-style-bounce {
              color: #ff9531;
              font-weight: bold;
            }
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 13px;
            }
          }
          .industry {
            /* display: block; */
            display: flex;
            justify-content: center;
            width: 100%;
            font-size: 14px;
            font-family: "robotomedium";
            font-weight: 500;
            white-space: nowrap;
            text-overflow: ellipsis;
            max-width: 100%;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
            }
          }
          .actions {
            ul {
              list-style: none;
              margin: 0 -10px;
              padding: 0;
              li {
                float: left;
                display: inline-block;
                padding: 0 3px;
                @media all and (max-width: 1200px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  padding: 0;
                }
                a {
                  padding: 0 5px;
                  text-align: center;
                  display: block;
                  @media all and (max-width: 1300px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                    padding: 0 3px;
                  }
                  svg {
                    width: 18px;
                    max-height: 12px;

                    path {
                      fill: #000000;
                      opacity: 1;
                    }
                    g {
                      opacity: 1;
                    }
                  }
                  span {
                    color: #5e7079;
                    font-size: 10px;
                    font-family: "helveticaneuebold";
                    font-weight: 700;
                    display: block;
                    text-align: center;
                  }
                }
              }
            }
          }
          .engagement {
            border-radius: 12px;
            background-color: #1971eb;
            color: #ffffff;
            font-size: 16px;
            font-family: "robotomedium";
            font-weight: 500;
            padding: 0px 10px;
            min-width: 80px;
            width: fit-content;
            float: right;
            margin-right: 3em;
            text-align: right;
            white-space: nowrap;
            img {
              float: left;
              margin-top: 7px;
            }
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
              img {
                max-width: 13px;
                margin-top: 5px;
              }
            }
          }
          .float-right {
            float: right;
            margin-left: 15px;
          }
        }
      }
    }
  }
`;
